import React, { useCallback, useEffect, useRef, useState } from 'react';
import '../styles/hover-celebration-logo.css';

export const CHARGE_DURATION = 1600;
const LOGO_OUTLINE = 'M56 2 H84 A26 26 0 0 1 110 28 V84 A26 26 0 0 1 84 110 H28 A26 26 0 0 1 2 84 V28 A26 26 0 0 1 28 2 H56 Z';

const colors = ['#ffd83d', '#ff896b', '#62b7d8', '#91bc8c'];
const confetti = Array.from({ length: 28 }, (_, index) => {
  const angle = (index / 28) * Math.PI * 2;
  const distance = 85 + (index % 4) * 15;
  const x = Math.cos(angle) * distance;
  const y = Math.sin(angle) * distance - 28;
  return {
    '--burst-x': `${x}px`,
    '--burst-y': `${y + 45}px`,
    '--burst-mid-x': `${x * 0.65}px`,
    '--burst-mid-y': `${y * 0.85}px`,
    '--burst-spin': `${(index % 2 ? 1 : -1) * (180 + index * 27)}deg`,
    '--burst-delay': `${(index % 4) * 22}ms`,
    '--burst-duration': `${850 + (index % 5) * 65}ms`,
    backgroundColor: colors[index % colors.length],
    borderRadius: index % 3 === 0 ? '50%' : '2px',
  };
});
export const CELEBRATION_DURATION = Math.max(...confetti.map(style =>
  parseFloat(style['--burst-delay']) + parseFloat(style['--burst-duration'])
));

export default function HoverCelebrationLogo({ src, alt, label, completeLabel = 'Ta-da!', className = '' }) {
  const [phase, setPhase] = useState('idle');
  const progressRef = useRef(null);
  const surfaceRef = useRef(null);
  const frameRef = useRef(null);
  const runRef = useRef(0);
  const activeRef = useRef(false);
  const finishedRef = useRef(false);
  const hoveredRef = useRef(false);
  const focusedRef = useRef(false);

  const reset = useCallback(() => {
    runRef.current += 1;
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
    activeRef.current = false;
    finishedRef.current = false;
    if (progressRef.current) progressRef.current.style.strokeDashoffset = '100';
    surfaceRef.current?.style.setProperty('--logo-charge-progress', '0');
    setPhase('idle');
  }, []);

  const begin = useCallback((replay = false) => {
    if (activeRef.current || (finishedRef.current && !replay)) return;
    const run = ++runRef.current;
    const started = performance.now();
    activeRef.current = true;
    finishedRef.current = false;
    if (progressRef.current) progressRef.current.style.strokeDashoffset = '100';
    surfaceRef.current?.style.setProperty('--logo-charge-progress', '0');
    setPhase('charging');

    const advance = timestamp => {
      if (run !== runRef.current) return;
      const progress = Math.min(1, Math.max(0, (timestamp - started) / CHARGE_DURATION));
      if (progressRef.current) progressRef.current.style.strokeDashoffset = String(100 * (1 - progress));
      surfaceRef.current?.style.setProperty('--logo-charge-progress', String(progress));
      if (progress === 1) {
        frameRef.current = null;
        activeRef.current = false;
        finishedRef.current = true;
        setPhase('complete');
      } else {
        frameRef.current = requestAnimationFrame(advance);
      }
    };
    frameRef.current = requestAnimationFrame(advance);
  }, []);

  useEffect(() => {
    if (phase !== 'complete') return;
    const run = runRef.current;
    const timeout = window.setTimeout(() => {
      if (run !== runRef.current) return;
      if (progressRef.current) progressRef.current.style.strokeDashoffset = '100';
      surfaceRef.current?.style.setProperty('--logo-charge-progress', '0');
      setPhase('idle');
    }, CELEBRATION_DURATION);
    return () => window.clearTimeout(timeout);
  }, [phase]);

  useEffect(() => {
    const cancelInteraction = () => {
      hoveredRef.current = false;
      focusedRef.current = false;
      reset();
    };
    const onVisibilityChange = () => { if (document.hidden) cancelInteraction(); };
    window.addEventListener('blur', cancelInteraction);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('blur', cancelInteraction);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      runRef.current += 1;
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    };
  }, [reset]);

  return <button
    type="button"
    className={`logo-celebration ${className}`}
    aria-label={label || alt}
    data-state={phase}
    onPointerEnter={event => {
      if (event.pointerType === 'touch') return;
      hoveredRef.current = true;
      begin();
    }}
    onPointerLeave={event => {
      if (event.pointerType === 'touch') return;
      hoveredRef.current = false;
      if (!focusedRef.current) reset();
    }}
    onFocus={event => {
      if (!event.currentTarget.matches(':focus-visible')) return;
      focusedRef.current = true;
      begin();
    }}
    onBlur={() => {
      focusedRef.current = false;
      if (!hoveredRef.current) reset();
    }}
    onClick={() => begin(true)}
  >
    <span ref={surfaceRef} className="logo-celebration-surface">
      <span className="logo-celebration-jitter">
        <img className="logo-celebration-image" src={src} alt={alt} draggable="false" />
      </span>
      <svg className="logo-celebration-ring" viewBox="0 0 112 112" aria-hidden="true">
        <path className="logo-celebration-track" d={LOGO_OUTLINE} />
        <path ref={progressRef} data-testid="logo-progress" className="logo-celebration-progress" d={LOGO_OUTLINE} pathLength="100" strokeDasharray="100 100" strokeDashoffset="100" />
      </svg>
    </span>
    {phase === 'complete' && <>
      <span className="logo-celebration-confetti" data-testid="logo-confetti" aria-hidden="true">
        {confetti.map((style, index) => <span key={index} className="logo-celebration-particle" style={style} />)}
      </span>
      <span className="logo-celebration-stars" aria-hidden="true"><span>✦</span><span>✧</span><span>✦</span><span>✧</span></span>
    </>}
    <span className="logo-celebration-status" role="status">{phase === 'complete' ? completeLabel : ''}</span>
  </button>;
}
